import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getPortfolioValuations, type PortfolioValuation } from '../api/portfolio-valuation';
import {
  PORTFOLIO_DETAIL_REFETCH_OPTIONS,
  PORTFOLIO_DETAIL_RETRY,
  PORTFOLIO_DETAIL_STALE_MS,
  portfolioDetailQueryKeys,
} from './use-portfolio-detail-queries';
import { calendarDayInFilterZone } from '../../../shared/lib/date-utils';
import type { ScopeContext } from '../../auth/model/types';

export const NAV_HISTORY_PERIODS = ['1M', '3M', '6M', 'YTD', '1Y', 'ALL'] as const;
export type NavHistoryPeriod = (typeof NAV_HISTORY_PERIODS)[number];

const MONTHS_BY_PERIOD: Partial<Record<NavHistoryPeriod, number>> = {
  '1M': 1,
  '3M': 3,
  '6M': 6,
  '1Y': 12,
};

function isoDatePart(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * `today` minus `months`, on the calendar, clamped to the last day of the target month —
 * a month before 31 May is 30 April, never "31 April" rolled over into May, which is
 * what `Date.setMonth` would answer. Pure calendar arithmetic on the parts, so the
 * viewer's zone never enters into it: the date filters are read in São Paulo, and
 * `today` already is.
 */
function calendarMonthsBefore(today: string, months: number): string {
  const [year, month, day] = today.split('-').map(Number);
  const monthIndex = year * 12 + (month - 1) - months;
  const targetYear = Math.floor(monthIndex / 12);
  const targetMonth = (monthIndex % 12) + 1;
  const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate();
  return isoDatePart(targetYear, targetMonth, Math.min(day, lastDayOfTargetMonth));
}

/**
 * The `after` bound a period selects, given the São Paulo calendar day it is asked on, or
 * `undefined` for the unbounded series. `before` is never sent: the series always runs up
 * to the latest day the backend has.
 *
 * `today` is `null` when the runtime's clock could not be composed into a calendar day
 * (`calendarDayInFilterZone`'s own contract). That is a reason to drop the bound, the same
 * way `endOfDayInFilterZone` does for an uncomposable date — never a reason to compose one
 * out of the empty string, which is how a YTD request would end up asking for the whole,
 * unbounded history under a pressed "YTD" button instead of failing to add a bound at all.
 */
export function navHistoryAfterDate(period: NavHistoryPeriod, today: string | null): string | undefined {
  if (today == null) return undefined;
  if (period === 'ALL') return undefined;
  if (period === 'YTD') return isoDatePart(Number(today.slice(0, 4)), 1, 1);
  const months = MONTHS_BY_PERIOD[period];
  if (months == null) return undefined;
  return calendarMonthsBefore(today, months);
}

/**
 * The series a chart is showing, together with the key that produced it. The component
 * reads `period` and `currency` off this rather than off the page's own state: when the
 * requested key failed and this is a retained earlier series, the two differ, and the
 * chart says so rather than labelling one period's series as another's.
 */
export interface NavHistorySeries {
  portfolioId: string;
  period: NavHistoryPeriod;
  currency: string;
  valuations: PortfolioValuation[];
}

/**
 * The NAV history read, keyed on portfolio, period and currency, so every key holds its
 * own answer: a request the reader has moved on from lands in the key it was asked for and
 * never in the one now on screen. That is what makes a mid-flight period switch safe — the
 * later period's data is the only thing its key can ever show.
 *
 * There is deliberately no `placeholderData` across keys, unlike the summary: a chart of
 * last month's series under a pressed "3M" button is a wrong chart, not a held figure, so
 * a period or currency not yet cached shows the skeleton while it loads.
 *
 * `lastSeries` mirrors `usePositionSummaryQuery`'s retention for the one gap TanStack
 * leaves: a key that has never succeeded has no `data` to retain on failure, while the
 * portfolio's history a moment ago is still perfectly good to keep on screen beside the
 * failure note. It is kept with the key that produced it, and ignored on a portfolio
 * mismatch, for the same cross-portfolio reason `samePortfolioOnly` exists.
 */
export function usePortfolioValuationQuery(
  sessionId: string | null,
  accountScope: ScopeContext | null,
  portfolioId: string | undefined,
  period: NavHistoryPeriod,
  currency: string | null,
) {
  const query = useQuery({
    queryKey: portfolioDetailQueryKeys.navHistory(portfolioId ?? '', period, currency ?? ''),
    queryFn: ({ signal }) => {
      const today = calendarDayInFilterZone(new Date().toISOString());
      return getPortfolioValuations(sessionId!, accountScope!, portfolioId!, {
        after: navHistoryAfterDate(period, today),
        currency: currency!,
      }, {
        isCancelled: () => signal.aborted,
      });
    },
    enabled: !!sessionId && !!accountScope && !!portfolioId && currency !== null,
    staleTime: PORTFOLIO_DETAIL_STALE_MS,
    retry: PORTFOLIO_DETAIL_RETRY,
    ...PORTFOLIO_DETAIL_REFETCH_OPTIONS,
  });

  const [lastSeries, setLastSeries] = useState<NavHistorySeries | null>(null);
  useEffect(() => {
    if (query.data && portfolioId && currency !== null) {
      setLastSeries({ portfolioId, period, currency, valuations: query.data });
    }
  }, [query.data, portfolioId, period, currency]);

  // Memoized so a render this hook's caller triggers for an unrelated reason (the 3s
  // booking poll, the 1.5s snapshot poll) hands the chart the same object it had last
  // time, rather than a fresh literal it has no way to tell from a real change — mirroring
  // how `detail.tsx` memoizes `scopeContext` for the same reason. Without this, every such
  // render re-triggers the chart's own `useMemo` (keyed on this series), re-sorting the
  // whole series and re-deriving the "now" point's `Date.now()` on every poll tick, which
  // continuously shifts the x-axis domain that point drives while nothing the reader asked
  // for actually changed.
  const currentSeries = useMemo<NavHistorySeries | null>(
    () => (query.data && portfolioId && currency !== null
      ? { portfolioId, period, currency, valuations: query.data }
      : null),
    [query.data, portfolioId, period, currency],
  );
  const stickySeries = lastSeries != null && lastSeries.portfolioId === portfolioId ? lastSeries : null;

  return {
    // For display: this key's series, falling back to the last one that succeeded for this
    // portfolio so a switch that never succeeded still has something to show.
    series: currentSeries ?? stickySeries,
    error: query.error,
    isError: query.isError,
    isLoading: query.isLoading || currency === null,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}
